import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  useMetricsUi,
  headCellSx,
  numCellSx,
  textCellSx,
  SectionHead,
} from "./metricsUi";
import {
  formatCount,
  formatCurrency,
  dayOfWeekLabel,
  shortDateLabel,
} from "./metricsFormat";
import DailySkuBreakdownTable from "./DailySkuBreakdownTable";

/**
 * Daily sales detail for the selected period (optionally filtered to a week).
 *
 * Clicking a non-zero-activity row expands that day's SKU breakdown inline,
 * directly beneath the clicked row, inside this same scrollable table — not
 * as a separate section elsewhere on the page. Only one day expands at a
 * time; `skuState` carries the loading/error/payload for whichever date is
 * currently selected.
 */
const DailyDetailTable = ({
  daily = [],
  weekFilter = null,
  onClearWeekFilter,
  allDaysSubtitle = "All days in the selected fiscal period",
  selectedDate = null,
  onSelectDay,
  skuState = null,
}) => {
  const ui = useMetricsUi();

  const rows = weekFilter
    ? daily.filter(
        (row) =>
          row.date >= weekFilter.start_date && row.date <= weekFilter.end_date,
      )
    : daily;

  if (!daily.length) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <SectionHead
        title="Daily detail"
        subtitle={
          weekFilter
            ? `${weekFilter.label} · ${weekFilter.start_date} – ${weekFilter.end_date}`
            : allDaysSubtitle
        }
        right={
          weekFilter ? (
            <Button
              size="small"
              onClick={onClearWeekFilter}
              sx={{
                fontSize: 12,
                textTransform: "none",
                color: ui.accent,
              }}
            >
              Show full period
            </Button>
          ) : null
        }
      />

      <TableContainer sx={{ ...ui.card, overflow: "auto", maxHeight: 460 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx(ui)}>Date</TableCell>
              <TableCell sx={headCellSx(ui)}>Day</TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Gross Sales
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Orders
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Units Sold
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const hasActivity = row.gross_sales || row.orders || row.units_sold;
              const clickable = !!(hasActivity && onSelectDay);
              const expanded = selectedDate === row.date;
              return (
                <React.Fragment key={row.date}>
                  <TableRow
                    hover={!!hasActivity}
                    onClick={clickable ? () => onSelectDay(row.date) : undefined}
                    sx={{
                      cursor: clickable ? "pointer" : "default",
                      backgroundColor: expanded ? ui.accentSoft : "transparent",
                      "&:hover td": {
                        backgroundColor: hasActivity
                          ? ui.surfaceAlt
                          : "transparent",
                      },
                      "&:last-child td": {
                        borderBottom: expanded ? undefined : "none",
                      },
                    }}
                  >
                    <TableCell
                      sx={textCellSx(ui, {
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: hasActivity ? 500 : 400,
                        color: hasActivity
                          ? ui.colors.textPrimary
                          : ui.colors.textTertiary,
                      })}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.4,
                        }}
                      >
                        {clickable ? (
                          <KeyboardArrowRightIcon
                            sx={{
                              fontSize: 15,
                              color: expanded
                                ? ui.accent
                                : ui.colors.textTertiary,
                              transition: "transform .15s ease",
                              transform: expanded
                                ? "rotate(90deg)"
                                : "none",
                            }}
                          />
                        ) : (
                          <Box sx={{ width: 15 }} />
                        )}
                        <span>{shortDateLabel(row.date)}</span>
                      </Box>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 11,
                          color: ui.colors.textTertiary,
                          ml: 0.75,
                        }}
                      >
                        {row.date}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={textCellSx(ui, {
                        fontSize: 11,
                        color: ui.colors.textTertiary,
                        letterSpacing: "0.04em",
                      })}
                    >
                      {dayOfWeekLabel(row.date)}
                    </TableCell>
                    <TableCell
                      sx={numCellSx(ui, {
                        fontWeight: hasActivity ? 600 : 400,
                        color: hasActivity
                          ? ui.colors.textPrimary
                          : ui.colors.textTertiary,
                      })}
                    >
                      {formatCurrency(row.gross_sales)}
                    </TableCell>
                    <TableCell
                      sx={numCellSx(ui, {
                        color: hasActivity
                          ? ui.colors.textPrimary
                          : ui.colors.textTertiary,
                      })}
                    >
                      {formatCount(row.orders)}
                    </TableCell>
                    <TableCell
                      sx={numCellSx(ui, {
                        color: hasActivity
                          ? ui.colors.textPrimary
                          : ui.colors.textTertiary,
                      })}
                    >
                      {formatCount(row.units_sold)}
                    </TableCell>
                  </TableRow>

                  {expanded && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{
                          p: 0,
                          borderBottom: `1px solid ${ui.colors.border}`,
                          backgroundColor: ui.surfaceAlt,
                        }}
                      >
                        <Box sx={{ px: 2, py: 1.25, pl: 4.5 }}>
                          <DailySkuBreakdownTable
                            loading={skuState?.loading}
                            error={skuState?.error}
                            payload={skuState?.payload}
                            onRetry={skuState?.onRetry}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DailyDetailTable;
